import { ChangeDetectionStrategy, Component, computed, inject, Input, signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import {
  BuildingSectionDetailDto,
  BuildingSectionSubtenantAssignmentDto,
  TenantFilterDto,
} from '@artemis-software/wr-api';
import TypeEnum = TenantFilterDto.TypeEnum;

export interface SubtenantAssignDialogResult {
  sectionId: string;
  tenantId: string;
}

@Component({
  standalone: false,
  selector: 'wr-subtenant-assign-dialog',
  templateUrl: './subtenant-assign-dialog.component.html',
  styleUrl: './subtenant-assign-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubtenantAssignDialogComponent {
  private readonly dialogRef = inject(NbDialogRef<SubtenantAssignDialogComponent>);

  @Input() sections: BuildingSectionDetailDto[] = [];
  @Input() existingAssignments: BuildingSectionSubtenantAssignmentDto[] = [];
  @Input() set presetSectionId(value: string | null) {
    if (value) {
      this.sectionId.setValue(value);
    }
  }

  readonly TenantType = TypeEnum;
  readonly sectionId = new FormControl<string | null>(null, { validators: [Validators.required] });
  readonly tenantId = new FormControl<string | null>(null, { validators: [Validators.required] });
  readonly submitted = signal(false);

  readonly duplicate = computed(() => {
    const s = this.sectionIdSignal();
    const t = this.tenantIdSignal();
    if (!s || !t) return false;
    return this.existingAssignments.some(a => a.sectionId === s && a.tenantId === t);
  });

  readonly canSubmit = computed(() => !!this.sectionIdSignal() && !!this.tenantIdSignal() && !this.duplicate());

  // bridge form controls into signals so computed() reacts
  private readonly sectionIdSignal = signal<string | null>(null);
  private readonly tenantIdSignal = signal<string | null>(null);

  constructor() {
    this.sectionId.valueChanges.subscribe(v => this.sectionIdSignal.set(v));
    this.tenantId.valueChanges.subscribe(v => this.tenantIdSignal.set(v));
  }

  sectionLabel(section: BuildingSectionDetailDto): string {
    const a = section.address;
    return `${a.street} ${a.number}, ${a.zipCode} ${a.city}`;
  }

  cancel() {
    this.dialogRef.close();
  }

  submit() {
    this.submitted.set(true);
    if (!this.canSubmit()) return;
    const result: SubtenantAssignDialogResult = {
      sectionId: this.sectionId.value!,
      tenantId: this.tenantId.value!,
    };
    this.dialogRef.close(result);
  }
}
